import React, { FC, ReactNode, useState } from "react";
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SxProps,
} from "@mui/material";
import { Action, resolveActionPreferences } from "./Action";
import { Layout } from "./Layout";
import { LayoutDirection } from "./LayoutDirection";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { isSelectableAction } from "./SelectableAction";
import { useClassName } from "./useResolveClassName";
import { ItemVariant } from "./ItemVariant";
import { useSxMerge } from "shared-ts-mui";
import { useResolveLayoutDirection } from "./useResolveLayoutDirection";
import { useResolveActionGroupColors } from "./useResolveItemColors";
import { useResolveItemNameWithHiddenMeasure } from "./useResolveItemNameWithHiddenMeasure";

export interface ActionListItemProps {
  item: Action;
  variant?: ItemVariant;
  layout: Layout;
  layoutDirection?: LayoutDirection;
  hideSelectionIcon?: boolean;
  hideLabel?: boolean;
  endAdornment?: ReactNode;
  sx?: SxProps;
}
export const ActionListItem: FC<ActionListItemProps> = (props) => {
  const { item, endAdornment } = props;
  // Use a callback ref + state so the element is available after first mount
  const [element, setElement] = useState<HTMLLIElement | null>(null);
  const { name, measureRef } = useResolveItemNameWithHiddenMeasure(
    item,
    element
  );
  const layoutDirection = useResolveLayoutDirection(props);
  const hideLabel = props.hideLabel ?? false;
  const preferences = resolveActionPreferences(item.preferences, props.layout);
  const hideSelectionIcon =
    props.hideSelectionIcon ?? preferences?.hideSelectionIcon ?? false;
  const selected = isSelectableAction(item) ? item.selected : undefined;
  const selectable = selected !== undefined;

  const classNameRoot = useClassName(props, [
    "Item",
    "Action",
    "ActionListItem",
  ]);

  const classNameButton = useClassName(props, ["ActionListItem-button"]);
  const actionGroupColors = useResolveActionGroupColors();

  const handleClick = () => {
    item.onExecute(item);
  };

  const sx = useSxMerge(props.sx, {
    "&.variant-rounded": {
      borderRadius: "50px",
    },
    "&.direction-column": {
      "&.layout-medium": {
        borderRadius: 3,
      },
    },
  });

  return (
    <ListItem
      ref={setElement}
      onClick={handleClick}
      className={classNameRoot}
      disablePadding
      disableGutters
      sx={sx}
    >
      {/* Hidden measurement span for pre-measuring text overflow before paint */}
      <span
        ref={measureRef}
        style={{
          position: "absolute",
          visibility: "hidden",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          font: "inherit",
          fontSize: "inherit",
          fontWeight: "inherit",
          letterSpacing: "inherit",
        }}
        aria-hidden="true"
      />
      <ListItemButton
        className={classNameButton}
        sx={{
          borderRadius: "inherit",
          ">.MuiListItemText-root": {
            ">.MuiTypography-root": {
              fontSize: "inherit",
              textAlign: "inherit",
            },
          },
          "&.layout-medium": {
            fontSize: "0.75rem",
            "&.direction-column": {
              ".MuiListItemIcon-root": {
                height: "32px",
                width: "56px",
              },
            },
          },
          "&.layout-compact": {
            fontSize: "0.75rem",
            "&.direction-column": {
              ".MuiListItemIcon-root": {
                height: "32px",
                width: "56px",
              },
            },
          },
          "&.direction-row": {
            textAlign: "left",
            ".MuiListItemText-root": {
              minHeight: "20px",
            },
            "&.selected": {
              color: actionGroupColors.primaryText,
              backgroundColor: actionGroupColors.selected,
            },
            "&:hover": {
              backgroundColor: selected
                ? actionGroupColors.focus
                : actionGroupColors.hover,
            },
          },
          "&.direction-column": {
            flexDirection: "column",
            textAlign: "center",
            alignItems: "center",
            gap: "4px",
            ".MuiListItemIcon-root": {
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "15px",
            },
            "&.selected": {
              backgroundColor: actionGroupColors.selected,
              color: actionGroupColors.selectedText,
              ".MuiListItemIcon-root": {
                backgroundColor: actionGroupColors.selected,
                color: selected
                  ? actionGroupColors.selectedText
                  : actionGroupColors.primaryText,
              },
            },
            "&:hover": {
              backgroundColor: actionGroupColors.hover,
              ".MuiListItemIcon-root": {
                backgroundColor: selected
                  ? actionGroupColors.focus
                  : actionGroupColors.selected,
              },
            },
          },
        }}
      >
        {selectable && !hideSelectionIcon && selected && (
          <ListItemIcon className={"CheckIcon"}>
            <CheckRoundedIcon />
          </ListItemIcon>
        )}
        {item.icon != null && <ListItemIcon>{item.icon}</ListItemIcon>}
        {!hideLabel && (
          <ListItemText
            inset={selectable && !hideSelectionIcon && !selected}
            primary={name}
          />
        )}
        {endAdornment != null && layoutDirection === "row" && endAdornment}
      </ListItemButton>
    </ListItem>
  );
};
